import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Autocomplete,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import holidayApiService, {
  WorkdayOverrideDto,
} from "../../services/holiday-api";
import officeLocationsApiService, {
  OfficeLocation,
} from "../../services/office-locations-api";

interface WorkdayOverridesCardProps {
  onSave?: () => void;
  hideHeader?: boolean;
  shouldLoad?: boolean;
}

export interface WorkdayOverridesCardRef {
  openAddDialog: () => void;
}

const WorkdayOverridesCard = forwardRef<
  WorkdayOverridesCardRef,
  WorkdayOverridesCardProps
>(({ onSave, hideHeader = false, shouldLoad = false }, ref) => {
  const { t } = useTranslation();
  const [overrides, setOverrides] = useState<WorkdayOverrideDto[]>([]);
  const [officeLocations, setOfficeLocations] = useState<OfficeLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [filterYear, setFilterYear] = useState<string>(
    new Date().getFullYear().toString(),
  );
  const [formData, setFormData] = useState<WorkdayOverrideDto>({
    overrideDate: "",
    dayOfWeek: 6,
    reason: "",
    officeLocations: [],
  });

  useEffect(() => {
    if (shouldLoad) {
      loadOverrides();
      loadOfficeLocations();
    }
  }, [shouldLoad]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openAddDialog: async () => {
      // Ensure office locations are loaded before opening dialog
      if (officeLocations.length === 0) {
        await loadOfficeLocations();
      }
      handleOpenDialog();
    },
  }));

  // Filter overrides by year
  const filteredOverrides = useMemo(() => {
    return overrides.filter((override) => {
      const overrideYear = override.overrideDate.split("-")[0];
      return overrideYear === filterYear;
    });
  }, [overrides, filterYear]);

  // Get unique years from overrides
  const availableYears = useMemo(() => {
    const years = new Set(overrides.map((o) => o.overrideDate.split("-")[0]));
    // Always include current year
    years.add(new Date().getFullYear().toString());
    return Array.from(years).sort().reverse();
  }, [overrides]);

  const loadOverrides = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await holidayApiService.getAllWorkdayOverrides();
      setOverrides(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("config.errorWorkdayOverrideLoad"),
      );
    } finally {
      setLoading(false);
    }
  };

  const loadOfficeLocations = async () => {
    try {
      const locations = await officeLocationsApiService.getAllOfficeLocations();
      setOfficeLocations(locations);
    } catch (err) {
      console.error(t("config.errorLocationLoad"), err);
    }
  };

  const getDayName = (dayOfWeek: number): string => {
    const days = [
      "",
      t("config.dayMonday"),
      t("config.dayTuesday"),
      t("config.dayWednesday"),
      t("config.dayThursday"),
      t("config.dayFriday"),
      t("config.daySaturday"),
      t("config.daySunday"),
    ];
    return days[dayOfWeek] || "";
  };

  const handleOpenDialog = (override?: WorkdayOverrideDto) => {
    if (override) {
      setEditingId(override.id || null);
      setFormData(override);
    } else {
      setEditingId(null);
      setFormData({
        overrideDate: "",
        dayOfWeek: 6,
        reason: "",
        officeLocations: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({
      overrideDate: "",
      dayOfWeek: 6,
      reason: "",
      officeLocations: [],
    });
  };

  const handleSaveOverride = async () => {
    if (!formData.overrideDate || formData.officeLocations.length === 0) {
      setError(t("config.workdayOverrideRequired"));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editingId) {
        await holidayApiService.updateWorkdayOverride(editingId, formData);
        setSuccess(t("config.workdayOverrideUpdated"));
      } else {
        await holidayApiService.createWorkdayOverride(formData);
        setSuccess(t("config.workdayOverrideCreated"));
      }

      await loadOverrides();
      handleCloseDialog();
      onSave?.();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("config.errorWorkdayOverride"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOverride = async (overrideId: number | undefined) => {
    if (!overrideId) return;

    if (window.confirm(t("config.deleteWorkdayOverrideConfirm"))) {
      try {
        setLoading(true);
        await holidayApiService.deleteWorkdayOverride(overrideId);
        setSuccess(t("config.workdayOverrideDeleted"));
        await loadOverrides();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t("config.errorWorkdayOverrideDelete"),
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const colorOptions = [
    { value: "#E74C3C", label: "Red" },
    { value: "#27AE60", label: "Green" },
    { value: "#F1C40F", label: "Yellow" },
    { value: "#5DADE2", label: "Blue" },
    { value: "#9B59B6", label: "Purple" },
    { value: "#F1948A", label: "Pink" },
    { value: "#1ABC9C", label: "Teal" },
    { value: "#F5CBA7", label: "Peach" },
  ];

  return (
    <Card
      sx={{
        mb: hideHeader ? 0 : 3,
        borderRadius: 2,
        boxShadow: hideHeader ? "none" : "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <CardContent sx={{ p: hideHeader ? 0 : 3 }}>
        {!hideHeader && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              📅 {t("config.workdayOverrides")}
            </Typography>
            <Tooltip title={t("config.addNewWorkdayOverride")}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                variant="contained"
                size="small"
              >
                {t("common.add")}
              </Button>
            </Tooltip>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Year Filter */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mt: 1, mb: 3 }}
        >
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>{t("common.year")}</InputLabel>
            <Select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              label={t("common.year")}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {loading && !overrides.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("config.date")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("config.dayOfWeek")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("config.reason")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("config.locations")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("config.color")}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>
                    {t("common.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOverrides.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        {t("config.noWorkdayOverrides")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOverrides.map((override) => (
                    <TableRow key={override.id} hover>
                      <TableCell>{override.overrideDate}</TableCell>
                      <TableCell>{getDayName(override.dayOfWeek)}</TableCell>
                      <TableCell>{override.reason}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {override.officeLocations.map((loc) => (
                            <Chip key={loc} label={loc} size="small" />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            backgroundColor: override.color || "#2196F3",
                            border: 1,
                            borderColor: "divider",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t("config.tooltipEdit")}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(override)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("config.tooltipDelete")}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteOverride(override.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingId
            ? t("config.editWorkdayOverride")
            : t("config.addWorkdayOverride")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <TextField
              label={t("config.date")}
              type="date"
              value={formData.overrideDate}
              onChange={(e) =>
                setFormData({ ...formData, overrideDate: e.target.value })
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label={t("config.reason")}
              value={formData.reason}
              onChange={(e) =>
                setFormData({ ...formData, reason: e.target.value })
              }
              fullWidth
              multiline
              rows={2}
              size="small"
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("config.color")}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                sx={{ mb: 1.5 }}
              >
                {colorOptions.map((option) => (
                  <Tooltip key={option.value} title={option.label}>
                    <Box
                      onClick={() =>
                        setFormData({ ...formData, color: option.value })
                      }
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        backgroundColor: option.value,
                        border: 2,
                        borderColor:
                          formData.color === option.value
                            ? "primary.main"
                            : "divider",
                        cursor: "pointer",
                        "&:hover": {
                          borderColor: "primary.main",
                        },
                      }}
                    />
                  </Tooltip>
                ))}
              </Stack>
              <TextField
                label={t("config.customColor")}
                value={formData.color || "#2196F3"}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setFormData({ ...formData, color: value });
                }}
                fullWidth
                size="small"
                placeholder="#XXXXXX"
                helperText="Enter hex color code (e.g., #E74C3C, #27AE60)"
                inputProps={{
                  maxLength: 7,
                  style: { fontFamily: "monospace" },
                }}
              />
            </Box>
            <Autocomplete
              multiple
              options={officeLocations}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.name
              }
              value={
                officeLocations.filter((loc) =>
                  formData.officeLocations.includes(loc.name),
                ) || []
              }
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  officeLocations: newValue.map((v) =>
                    typeof v === "string" ? v : v.name,
                  ),
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("config.locations")}
                  size="small"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSaveOverride}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

WorkdayOverridesCard.displayName = "WorkdayOverridesCard";

export default React.memo(WorkdayOverridesCard);
